import {
  rpIsDTNARPNumber,
  rpIsSmartplex,
  rpIsPTO,
} from 'utils/referenceParameter'
import {
  O_MLOGIC,
  O_TIMER,
  O_SLOTCOPY,
  VEHICLE_TYPE_M2,
  VEHICLE_TYPE_CHEC_PLUS,
} from 'utils/constants'
import strings from 'utils/strings.js'
import { equals } from 'ramda'
import { has } from 'utils/immutable'

// Roles are one of the SUPER...TECHNICIAN access levels,
// plus any or all of the special roles, currently INTERNAL and CHECPLUS.
// SUPER is NOT fully implemented and is not used.
export const SUPER = 'superAdmin'
export const ADMIN = 'admin'
export const PLUS_ADMIN = 'plusAdmin'
export const ENGINEER = 'engineer'
export const DEVELOPER = 'developer'
export const TECHNICIAN = 'technician'
export const BUILDER = 'builder'
export const VIEWER = 'viewer'
export const PLUS_VIEWER = 'plusViewer'
// Add to userAccessLevelForDisplay() if adding here.
export const INTERNAL = 'internal'
export const CHECPLUS = 'plus'
// for maximum render testing (see above on SUPER):
export const ALLPOWERFUL = [INTERNAL, CHECPLUS, ADMIN]

// -- permissions
export const READ_SP = 'READ_SP'
export const READ_RP = 'READ_RP'
export const VIEW_CIRCUITS = 'VIEW_CIRCUITS'
export const ADD_REMOVE_VEHICLE_RPS = 'ADD_REMOVE_VEHICLE_RPS'
export const VIEW_VEHICLES = 'VIEW_VEHICLES'
export const PIN_VEHICLES = 'PIN_VEHICLES'
export const DUPLICATE_RP = 'DUPLICATE_RP'
export const DUPLICATE_NON_26_RP = 'DUPLICATE_NON_26_RP'
export const DUPLICATE_SMARTPLEX_RP = 'DUPLICATE_SMARTPLEX_RP'
export const LINK_SPS_TO_NON_26_RPS = 'LINK_SPS_TO_NON_26_RPS'
export const LINK_SPS_TO_ALL_RPS = 'LINK_SPS_TO_ALL_RPS'
export const ACTIVATE_SMARTPLEX_SPS = 'ACTIVATE_SMARTPLEX_SPS'
export const ACTIVATE_NON_26_RP = 'ACTIVATE_NON_26_RP'
export const ACTIVATE_RP = 'ACTIVATE_RP'
export const EDIT_MLOGIC_WITH_35MP1_MASTER_NO =
  'EDIT_MLOGIC_WITH_35MP1_MASTER_NO'
export const ASSIGN_CIRCUIT = 'ASSIGN_CIRCUIT'
export const DUPLICATE_ALL_SPS = 'DUPLICATE_SPS'
export const DUPLICATE_NON_26_SPS = 'DUPLICATE_NON_26_SPS'
export const ACTIVATE_ALL_SPS = 'ACTIVATE_ALL_SPS'
export const EDIT_RPS = 'EDIT_RPS'
export const EDIT_SMARTPLEX_RPS = 'EDIT_SMARTPLEX_RPS'
export const CREATE_NEW_RELEASE = 'CREATE_NEW_RELEASE'
export const USER_ADMINISTRATION = 'USER_ADMINISTRATION'
export const READ_SUPER_PERMISSIONS = 'READ SUPER PERMISSIONS'
export const READ_ADMIN_PERMISSIONS = 'READ ADMIN PERMISSIONS'
export const READ_ENGINEER_PERMISSIONS = 'READ ENGINEER PERMISSIONS'
export const READ_DEVELOPER_PERMISSIONS = 'READ DEVELOPER PERMISSIONS'
export const READ_TECHNICIAN_PERMISSIONS = 'READ TECHNICIAN PERMISSIONS'
export const READ_BUILDER_PERMISSIONS = 'READ_BUILDER_PERMISSIONS'

const BUILDER_PERMISSIONS = [READ_RP, READ_SP, READ_BUILDER_PERMISSIONS]

const TECHNICIAN_PERMISSIONS = [
  ...BUILDER_PERMISSIONS,
  VIEW_CIRCUITS,
  ADD_REMOVE_VEHICLE_RPS,
  VIEW_VEHICLES,
  PIN_VEHICLES,
  READ_TECHNICIAN_PERMISSIONS,
]

const DEVELOPER_PERMISSIONS = [
  ...TECHNICIAN_PERMISSIONS,
  DUPLICATE_SMARTPLEX_RP,
  LINK_SPS_TO_NON_26_RPS,
  ACTIVATE_SMARTPLEX_SPS,
  EDIT_SMARTPLEX_RPS,
  EDIT_MLOGIC_WITH_35MP1_MASTER_NO,
  DUPLICATE_NON_26_SPS,
  ASSIGN_CIRCUIT,
  READ_DEVELOPER_PERMISSIONS,
]

const INTERNAL_DEVELOPER_PERMISSIONS = [...DEVELOPER_PERMISSIONS]

const ENGINEER_PERMISSIONS = [
  ...DEVELOPER_PERMISSIONS,
  DUPLICATE_NON_26_RP,
  DUPLICATE_ALL_SPS,
  ACTIVATE_RP,
  EDIT_RPS,
  READ_ENGINEER_PERMISSIONS,
]

const INTERNAL_ENGINEER_PERMISSIONS = [
  ...ENGINEER_PERMISSIONS,
  ...INTERNAL_DEVELOPER_PERMISSIONS,
  LINK_SPS_TO_ALL_RPS,
  ACTIVATE_ALL_SPS,
  DUPLICATE_RP,
]

const ADMIN_PERMISSIONS = [
  ...ENGINEER_PERMISSIONS,
  CREATE_NEW_RELEASE,
  USER_ADMINISTRATION,
  READ_ADMIN_PERMISSIONS,
]

const INTERNAL_ADMIN_PERMISSIONS = [
  ...ADMIN_PERMISSIONS,
  ...INTERNAL_ENGINEER_PERMISSIONS,
]

const SUPER_PERMISSIONS = [...ADMIN_PERMISSIONS, READ_SUPER_PERMISSIONS]

// At this time, access level roles are additive.  Any given permission is available to the designated access level as well as all higher access levels.
// In order to determine whether a user is allowed to do something... userHasPermission(user.appRole, PERMISSION_CONSTANT)
// To define new permissions, simply define a new constant that represents the permission, and add it to the appropriate array below.
// Users with multiple access levels will be provisioned with the higher, least restrictive one.
// Permissions around parameters is more nuanced, and handled by special functions below.

const getUserRole = (appRoles) => ({
  isSuper: userHasRole(appRoles, SUPER),
  isInternalAdmin: userHasRole(appRoles, ADMIN) && userIsInternal(appRoles),
  isAdmin: userHasRole(appRoles, ADMIN),
  isInternalEngineer:
    userHasRole(appRoles, ENGINEER) && userIsInternal(appRoles),
  isEngineer: userHasRole(appRoles, ENGINEER),
  isInternalDeveloper:
    userHasRole(appRoles, DEVELOPER) && userIsInternal(appRoles),
  isDeveloper: userHasRole(appRoles, DEVELOPER),
  isTechnician: userHasRole(appRoles, TECHNICIAN),
  isChecPlus: userHasRole(appRoles, CHECPLUS),
  isBuilder: userHasRole(appRoles, BUILDER),
  isViewer: userHasRole(appRoles, VIEWER),
  isPlusViewer: userHasRole(appRoles, PLUS_VIEWER),
  isEngineerOrAdmin:
    userHasRole(appRoles, ENGINEER) || userHasRole(appRoles, ADMIN),
})

const getRolePermissions = (appRoles) => {
  const userRole = getUserRole(appRoles)
  if (userRole.isAdmin) return SUPER_PERMISSIONS
  if (userRole.isInternalAdmin) return INTERNAL_ADMIN_PERMISSIONS
  if (userRole.isAdmin) return ADMIN_PERMISSIONS
  if (userRole.isInternalEngineer) return INTERNAL_ENGINEER_PERMISSIONS
  if (userRole.isEngineer) return ENGINEER_PERMISSIONS
  if (userRole.isInternalDeveloper) return INTERNAL_DEVELOPER_PERMISSIONS
  if (userRole.isDeveloper) return DEVELOPER_PERMISSIONS
  if (userRole.isTechnician) return TECHNICIAN_PERMISSIONS
  return BUILDER_PERMISSIONS
}

export const userHasPermission = (appRoles, permission) =>
  getRolePermissions(appRoles).includes(permission)

export const userMayAddOrRemoveFeatures = (appRoles, checkVehicleProp) => {
  const userRole = getUserRole(appRoles)
  // Builder can only view and not edit pre-production vehicles
  // Builder cannot view serach or view post-production vehicle
  // All edit, duplicate, save buttons for features and parameters should be disabled
  if (userRole.isBuilder || checkVehicleProp) return false

  return true
}

export const userMayEditPneumatics = (appRoles) => {
  const userRole = getUserRole(appRoles)
  // Builder can only view and not edit pre-production vehicles
  // Builder cannot view serach or view post-production vehicle
  // Builder cannot edit Pneumatics
  if (userRole.isBuilder) return false

  return true
}

export const userIsEngineerOrAdmin = (appRoles) => {
  const userRole = getUserRole(appRoles)

  return userRole.isEngineerOrAdmin
}

const devEditableTypes = [O_MLOGIC, O_SLOTCOPY, O_TIMER]
const devDuplicableTypes = [O_MLOGIC, O_SLOTCOPY]
const hasLinkedSmartplexOrPTO = (linkedRPs) =>
  linkedRPs.some((rp) => rpIsSmartplex(rp) || rpIsPTO(rp))
const hasLinked26 = (linkedRPs) => linkedRPs.some((rp) => rpIsDTNARPNumber(rp))
const checkLinkedSmartplexOrPtoOrLinkedRPs = (linkedRPs, linkRestriction) =>
  linkedRPs.length === 0 ||
  (hasLinkedSmartplexOrPTO(linkedRPs) && linkRestriction)

export const hasDevDuplicateSPAccess = (
  sp,
  linkedRPs,
  linkRestriction = true
) =>
  sp &&
  sp.supportParameterType &&
  devDuplicableTypes.includes(sp.supportParameterType)
    ? checkLinkedSmartplexOrPtoOrLinkedRPs(linkedRPs, linkRestriction)
    : sp.supportParameterType === O_TIMER

const hasDeveloperSPAccess = (sp, linkedRPs, linkRestriction = true) =>
  sp &&
  sp.supportParameterType &&
  devEditableTypes.includes(sp.supportParameterType) &&
  checkLinkedSmartplexOrPtoOrLinkedRPs(linkedRPs, linkRestriction)

export const userMayEditSP = (appRoles, sp, linkedRPs) => {
  const userRole = getUserRole(appRoles)
  // Internal Engineers and Admins may edit anything.
  if (userRole.isInternalEngineer || userRole.isInternalAdmin) return true

  // Internal Developers may edit any SP of type O-MLOGIC, O-TIMER, or O-SLOTCOPY
  // that is linked to a smartplex RP (or has no links)
  if (userRole.isInternalDeveloper) return hasDeveloperSPAccess(sp, linkedRPs)

  // External Engineers and Admins may edit SPs of any type, so long as
  // they are NOT linked to a 26 rp.
  if (userRole.isEngineer || userRole.isAdmin)
    return linkedRPs.length === 0 || !hasLinked26(linkedRPs)

  // External Developers may edit SPs of type O-MLOGIC, O-TIMER, or O-SLOTCOPY
  // that are linked to nothing, or are linked to smartplex rps (and NOT linked to any 26 RPs)
  if (userRole.isDeveloper)
    return hasDeveloperSPAccess(sp, linkedRPs, !hasLinked26(linkedRPs))

  return false
}

export const userMayDuplicateSP = (appRoles, sp, linkedRPs) => {
  const userRole = getUserRole(appRoles)
  // Internal and External Engineers and Admins may duplicate anything.
  if (userRole.isEngineer || userRole.isAdmin) return true

  // Internal and External Developers may duplicate any SP of type O-MLOGIC or O-SLOTCOPY
  // that is linked to a smartplex RP (or has no links)
  // Internal and External Developers may duplicate SP of type O-TIMER regardless of linked RPs or linked smartplex RPs
  if (userRole.isDeveloper) return hasDevDuplicateSPAccess(sp, linkedRPs)

  return false
}

export const userMayActivateSP = (appRoles, sp, linkedRPs) => {
  const userRole = getUserRole(appRoles)
  // Internal Engineers and Admins may activate anything.
  if (userRole.isInternalEngineer || userRole.isInternalAdmin) return true

  // Internal Developers may activate any SP of type O-MLOGIC, O-TIMER, or O-SLOTCOPY
  // that is linked to a smartplex RP (or has no links)
  if (userRole.isInternalDeveloper) return hasDeveloperSPAccess(sp, linkedRPs)

  // External Engineers and Admins may activate SPs of any type, so long as
  // they are NOT linked to a 26 rp.
  if (userRole.isEngineer || userRole.isAdmin)
    return linkedRPs.length === 0 || !hasLinked26(linkedRPs)

  // External Developers may activate SPs of type O-MLOGIC, O-TIMER, or O-SLOTCOPY
  // that are linked to nothing, or are linked to smartplex rps (and NOT linked to any 26 RPs)
  if (userRole.isDeveloper)
    return hasDeveloperSPAccess(sp, linkedRPs, !hasLinked26(linkedRPs))

  return false
}

// Returns bool indicating whether the user has been accorded a particular role.
export const userHasRole = (appRoles, role) => appRoles.includes(role)

// Returns bool indicating whether or not the user is considered internal to DTNA.
export const userIsInternal = (appRoles) => appRoles.includes(INTERNAL)

// Returns bool indicating whether or not the user has the CHEC_DEV role for feature flags.
export const userIsChecDev = (appRoles) => appRoles.includes('checDev')

// New pattern to support user no-permission notices: !== true is error message.

export const userIsInternalAdmin = (
  appRoles,
  returnDescriptionOnFalse = false
) => {
  if (getUserRole(appRoles).isInternalAdmin) return true
  return returnDescriptionOnFalse ? strings.is_checplus : false
}
export const userMayAlert = userIsInternalAdmin

export const userIsChecPlus = (appRoles, returnDescriptionOnFalse = false) => {
  const userRole = getUserRole(appRoles)
  if (userRole.isChecPlus) return true
  return returnDescriptionOnFalse ? strings.is_checplus : false
}

export const userIsInternalEngineer = (
  appRoles,
  returnDescriptionOnFalse = false
) => {
  const userRole = getUserRole(appRoles)
  if (userRole.isInternalAdmin || userRole.isInternalEngineer) return true
  return returnDescriptionOnFalse ? strings.is_internal_engineer : false
}
export const userIsViewer = (appRoles) => {
  if (getUserRole(appRoles).isViewer) return true
  return false
}

export const userMayViewCTPMapping = userIsInternalEngineer

export const userMayAddComment = (appRights, vehicleType) => {
  const isM2 = equals(VEHICLE_TYPE_M2)
  const isChecPlus = equals(VEHICLE_TYPE_CHEC_PLUS)

  return (
    (isM2(vehicleType) && has('create_vehicle_comment', appRights)) ||
    (isChecPlus(vehicleType) && has('plus_create_vehicle_comment', appRights))
  )
}
